import { Currency, CurrencyAmount, Pair, Token, Trade, TokenAmount, JSBI, Price } from '@uniswap/sdk'
import flatMap from 'lodash.flatmap'
import { useMemo } from 'react'

import { BASES_TO_CHECK_TRADES_AGAINST, CUSTOM_BASES } from '../constants'
import { PairState, usePairs } from '../data/Reserves'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { isTaxToken, getTotalSellTax, getTotalBuyTax } from '../utils/dynamicTaxToken'

import { useActiveWeb3React } from './index'

function useAllCommonPairs(currencyA?: Currency, currencyB?: Currency): Pair[] {
  const { chainId } = useActiveWeb3React()

  const bases: Token[] = chainId ? BASES_TO_CHECK_TRADES_AGAINST[chainId] : []

  const [tokenA, tokenB] = chainId
    ? [wrappedCurrency(currencyA, chainId), wrappedCurrency(currencyB, chainId)]
    : [undefined, undefined]

  const basePairs: [Token, Token][] = useMemo(
    () =>
      flatMap(bases, (base): [Token, Token][] => bases.map(otherBase => [base, otherBase])).filter(
        ([t0, t1]) => t0.address !== t1.address
      ),
    [bases]
  )

  const allPairCombinations: [Token, Token][] = useMemo(
    () =>
      tokenA && tokenB
        ? [
            // the direct pair
            [tokenA, tokenB],
            // token A against all bases
            ...bases.map((base): [Token, Token] => [tokenA, base]),
            // token B against all bases
            ...bases.map((base): [Token, Token] => [tokenB, base]),
            // each base against all bases
            ...basePairs
          ]
            .filter((tokens): tokens is [Token, Token] => Boolean(tokens[0] && tokens[1]))
            .filter(([t0, t1]) => t0.address !== t1.address)
            .filter(([tokenA, tokenB]) => {
              if (!chainId) return true
              const customBases = CUSTOM_BASES[chainId]
              if (!customBases) return true

              const customBasesA: Token[] | undefined = customBases[tokenA.address]
              const customBasesB: Token[] | undefined = customBases[tokenB.address]

              if (!customBasesA && !customBasesB) return true

              if (customBasesA && !customBasesA.find(base => tokenB.equals(base))) return false
              if (customBasesB && !customBasesB.find(base => tokenA.equals(base))) return false

              return true
            })
        : [],
    [tokenA, tokenB, bases, basePairs, chainId]
  )

  const allPairs = usePairs(allPairCombinations)

  // only pass along valid pairs, non-duplicated pairs
  return useMemo(
    () =>
      Object.values(
        allPairs
          // filter out invalid pairs
          .filter((result): result is [PairState.EXISTS, Pair] => Boolean(result[0] === PairState.EXISTS && result[1]))
          // filter out duplicated pairs
          .reduce<{ [pairAddress: string]: Pair }>((memo, [, curr]) => {
            memo[curr.liquidityToken.address] = memo[curr.liquidityToken.address] ?? curr
            return memo
          }, {})
      ),
    [allPairs]
  )
}

/**
 * Returns the best trade for the exact amount of tokens in to the given token out
 */
export function useTradeExactIn(currencyAmountIn?: CurrencyAmount, currencyOut?: Currency): Trade | null {
  const allowedPairs = useAllCommonPairs(currencyAmountIn?.currency, currencyOut)
  return useMemo(() => {
    if (currencyAmountIn && currencyOut && allowedPairs.length > 0) {
      return (
        Trade.bestTradeExactIn(allowedPairs, currencyAmountIn, currencyOut, { maxHops: 3, maxNumResults: 1 })[0] ?? null
      )
    }
    return null
  }, [allowedPairs, currencyAmountIn, currencyOut])
}

/**
 * Calculate tax-adjusted amounts for both input and output
 */
export function getTaxAdjustedAmounts(
  trade: Trade | null
): {
  adjustedInputAmount: CurrencyAmount | null
  adjustedOutputAmount: CurrencyAmount | null
} {
  if (!trade) {
    return { adjustedInputAmount: null, adjustedOutputAmount: null }
  }

  const inputToken = trade.inputAmount.currency instanceof Token ? trade.inputAmount.currency : undefined
  const outputToken = trade.outputAmount.currency instanceof Token ? trade.outputAmount.currency : undefined

  let adjustedInputAmount = trade.inputAmount
  let adjustedOutputAmount = trade.outputAmount

  // Handle selling tax tokens
  if (inputToken && isTaxToken(inputToken)) {
    const sellTax = getTotalSellTax(inputToken)

    console.log(`Calculating tax-adjusted amounts for selling ${inputToken.symbol}:`, {
      originalInput: trade.inputAmount.toSignificant(6),
      originalOutput: trade.outputAmount.toSignificant(6),
      sellTaxPercent: sellTax.toFixed(4)
    })

    // Reduce output amount proportionally to sell tax
    const taxMultiplier = JSBI.subtract(JSBI.BigInt(10000), sellTax.numerator)
    const adjustedOutputRaw = JSBI.divide(JSBI.multiply(trade.outputAmount.raw, taxMultiplier), JSBI.BigInt(10000))

    adjustedOutputAmount =
      trade.outputAmount instanceof TokenAmount
        ? new TokenAmount(trade.outputAmount.token, adjustedOutputRaw)
        : CurrencyAmount.ether(adjustedOutputRaw)
  }

  // Handle buying tax tokens
  if (outputToken && isTaxToken(outputToken)) {
    const buyTax = getTotalBuyTax(outputToken)

    console.log(`Calculating tax-adjusted amounts for buying ${outputToken.symbol}:`, {
      originalInput: trade.inputAmount.toSignificant(6),
      originalOutput: trade.outputAmount.toSignificant(6),
      buyTaxPercent: buyTax.toFixed(4)
    })

    // Reduce output amount by buy tax
    const taxMultiplier = JSBI.subtract(JSBI.BigInt(10000), buyTax.numerator)
    const adjustedOutputRaw = JSBI.divide(JSBI.multiply(trade.outputAmount.raw, taxMultiplier), JSBI.BigInt(10000))

    adjustedOutputAmount =
      trade.outputAmount instanceof TokenAmount
        ? new TokenAmount(trade.outputAmount.token, adjustedOutputRaw)
        : CurrencyAmount.ether(adjustedOutputRaw)
  }

  return { adjustedInputAmount, adjustedOutputAmount }
}

/**
 * Calculate tax-adjusted execution price
 */
export function getTaxAdjustedExecutionPrice(trade: Trade | null): any {
  if (!trade) return undefined

  const { adjustedInputAmount, adjustedOutputAmount } = getTaxAdjustedAmounts(trade)

  // If we have tax adjustments, create a new price using adjusted amounts
  if (adjustedInputAmount !== trade.inputAmount || adjustedOutputAmount !== trade.outputAmount) {
    try {
      // Create a proper Price object with correct baseCurrency and quoteCurrency
      const inputCurrency = trade.inputAmount.currency
      const outputCurrency = trade.outputAmount.currency
      const adjustedInput = adjustedInputAmount || trade.inputAmount
      const adjustedOutput = adjustedOutputAmount || trade.outputAmount

      // Create Price from adjusted amounts
      const adjustedPrice = new Price(inputCurrency, outputCurrency, adjustedInput.raw, adjustedOutput.raw)

      console.log('Tax-adjusted price calculation:', {
        originalPrice: trade.executionPrice.toSignificant(6),
        adjustedPrice: adjustedPrice.toSignificant(6),
        originalInput: trade.inputAmount.toSignificant(6),
        originalOutput: trade.outputAmount.toSignificant(6),
        adjustedInput: adjustedInput.toSignificant(6),
        adjustedOutput: adjustedOutput.toSignificant(6),
        baseCurrency: adjustedPrice.baseCurrency.symbol,
        quoteCurrency: adjustedPrice.quoteCurrency.symbol
      })

      return adjustedPrice
    } catch (error) {
      console.warn('Failed to calculate tax-adjusted price:', error)
      return trade.executionPrice
    }
  }

  return trade.executionPrice
}

/**
 * Calculate the actual output amount considering tax tokens (legacy function)
 */
export function getTaxAdjustedOutputAmount(trade: Trade | null): CurrencyAmount | null {
  if (!trade) return null

  const inputToken = trade.inputAmount.currency instanceof Token ? trade.inputAmount.currency : undefined
  const outputToken = trade.outputAmount.currency instanceof Token ? trade.outputAmount.currency : undefined

  // If selling a tax token, reduce the output based on the tax
  if (inputToken && isTaxToken(inputToken)) {
    const sellTax = getTotalSellTax(inputToken)

    console.log(`Calculating tax-adjusted output for selling ${inputToken.symbol}:`, {
      originalInput: trade.inputAmount.toSignificant(6),
      originalOutput: trade.outputAmount.toSignificant(6),
      sellTaxPercent: sellTax.toFixed(4),
      sellTaxBasisPoints: sellTax.numerator.toString()
    })

    // When selling a tax token, the tax reduces the effective input amount
    // which proportionally reduces the output amount
    const taxMultiplier = JSBI.subtract(JSBI.BigInt(10000), sellTax.numerator)
    const adjustedOutputAmount = JSBI.divide(JSBI.multiply(trade.outputAmount.raw, taxMultiplier), JSBI.BigInt(10000))

    const adjustedOutput =
      trade.outputAmount instanceof TokenAmount
        ? new TokenAmount(trade.outputAmount.token, adjustedOutputAmount)
        : CurrencyAmount.ether(adjustedOutputAmount)

    console.log(`Tax-adjusted output: ${adjustedOutput.toSignificant(6)}`)

    return adjustedOutput
  }

  // If buying a tax token, reduce the output based on buy tax
  if (outputToken && isTaxToken(outputToken)) {
    const buyTax = getTotalBuyTax(outputToken)

    console.log(`Calculating tax-adjusted output for buying ${outputToken.symbol}:`, {
      originalOutput: trade.outputAmount.toSignificant(6),
      buyTaxPercent: buyTax.toFixed(4),
      buyTaxBasisPoints: buyTax.numerator.toString()
    })

    // When buying a tax token, the tax reduces the amount you actually receive
    const taxMultiplier = JSBI.subtract(JSBI.BigInt(10000), buyTax.numerator)
    const afterTaxOutputAmount = JSBI.divide(JSBI.multiply(trade.outputAmount.raw, taxMultiplier), JSBI.BigInt(10000))

    const adjustedOutput =
      trade.outputAmount instanceof TokenAmount
        ? new TokenAmount(trade.outputAmount.token, afterTaxOutputAmount)
        : CurrencyAmount.ether(afterTaxOutputAmount)

    console.log(`Tax-adjusted output: ${adjustedOutput.toSignificant(6)}`)

    return adjustedOutput
  }

  return trade.outputAmount
}

/**
 * Returns the best trade for the token in to the exact amount of token out
 */
export function useTradeExactOut(currencyIn?: Currency, currencyAmountOut?: CurrencyAmount): Trade | null {
  const allowedPairs = useAllCommonPairs(currencyIn, currencyAmountOut?.currency)

  return useMemo(() => {
    if (currencyIn && currencyAmountOut && allowedPairs.length > 0) {
      return (
        Trade.bestTradeExactOut(allowedPairs, currencyIn, currencyAmountOut, { maxHops: 3, maxNumResults: 1 })[0] ??
        null
      )
    }
    return null
  }, [allowedPairs, currencyIn, currencyAmountOut])
}
